import { Search } from "@carbon/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { z } from "zod";

import { companyMembersQueryOptions } from "@/api/companyMembers";
import { formSummariesQueryOptions } from "@/api/formSummaries";
import { listCampaignsQueryOptions } from "@/api/listCampaigns";
import { CampaignReportCard } from "@/components/admin/campaignReportCard";
import { CampaignReportCardSkeleton } from "@/components/admin/campaignReportCardSkeleton";
import { MemberCard } from "@/components/admin/memberCard";
import { ClosingDateFilter } from "@/components/admin/reports/closingDateFilter";
import { ReportStatusTabs } from "@/components/admin/reports/reportStatusTabs";
import { DashboardSectionTitle } from "@/components/admin/sectionTitle";
import { Button } from "@/components/input/button";
import {
  CampaignGroupsState,
  PENDING_CAMPAIGN_GROUPS,
  useCampaignGroups,
} from "@/hooks/useCampaignGroups";
import { Campaign } from "@/schemas/campaign";
import { Employee } from "@/schemas/employee";
import { EmployeeId } from "@/schemas/employeeId";
import {
  closingCounts,
  closingFilterFromSearch,
  closingFilterToSearch,
  listedYears,
  matchesClosing,
  matchesSearch,
  matchesStatus,
  reportStatusOf,
  statusCounts,
} from "@/util/campaignReport";
import { IsoDateSchema, lenient } from "@/util/lenient";

// Filters live in the URL so they survive opening a campaign or a member report and coming back,
// reloads and shared links (specs/008-campaign-reports-page FR-017). Defaults stay out of the URL.
const ReportsSearchSchema = z.object({
  status: lenient(z.enum(["ready", "published", "in-progress"])),
  q: lenient(z.string()),
  // Closing-date preset (a year is a number: `?closing=2025`); a custom range is `from`/`to` instead.
  closing: lenient(
    z.union([
      z.enum(["3m", "12m", "earlier"]),
      z.number().int().min(1000).max(9999),
    ]),
  ),
  from: lenient(IsoDateSchema),
  to: lenient(IsoDateSchema),
});

type ReportsSearch = z.infer<typeof ReportsSearchSchema>;

const CLEARED_FILTERS: Required<{ [Key in keyof ReportsSearch]: undefined }> = {
  status: undefined,
  q: undefined,
  closing: undefined,
  from: undefined,
  to: undefined,
};

export const Route = createFileRoute(
  "/dashboard/_authenticated/admin/reports/",
)({
  component: RouteComponent,
  staticData: { pageWidth: "wide" },
  validateSearch: ReportsSearchSchema,
});

const SKELETON_KEYS = ["a", "b", "c", "d", "e", "f"];

const inputClassName = `
  h-10 rounded-md border border-neutral-400 bg-[canvas] px-3 py-2.5 text-base
  outline-none
  placeholder:text-neutral-500
`;

function RouteComponent() {
  const search = Route.useSearch();
  const status = search.status ?? "all";
  const query = search.q ?? "";
  const navigate = Route.useNavigate();
  const updateFilters = useCallback(
    (patch: Partial<ReportsSearch>) => {
      navigate({
        search: (previous) => ({ ...previous, ...patch }),
        replace: true,
      });
    },
    [navigate],
  );

  const [memberSearch, setMemberSearch] = useState("");
  // One "today" per visit: report statuses and date presets depend on it.
  const today = useMemo(() => new Date(), []);

  const {
    data: campaignsPage,
    isLoading: isLoadingCampaigns,
    isError: isCampaignsError,
    refetch: refetchCampaigns,
  } = useQuery(listCampaignsQueryOptions());
  const { data: membersPage, isLoading: isLoadingMembers } = useQuery(
    companyMembersQueryOptions(),
  );
  const { data: forms } = useQuery(formSummariesQueryOptions());

  const campaignGroups = useCampaignGroups(
    campaignsPage?.content,
    membersPage?.content,
    forms,
  );

  // Most recently closing first; drafts (no end date) last.
  const campaigns = useMemo(
    () =>
      (campaignsPage?.content ?? []).toSorted((a, b) =>
        b.endsAt.localeCompare(a.endsAt),
      ),
    [campaignsPage?.content],
  );

  const closing = useMemo(
    () =>
      closingFilterFromSearch({
        closing: search.closing,
        from: search.from,
        to: search.to,
      }),
    [search.closing, search.from, search.to],
  );
  const closingContext = useMemo(
    () => ({ today, listedYears: listedYears(campaigns) }),
    [today, campaigns],
  );

  // Tab counts follow search + closing date; preset counts follow search + status (FR-016).
  const tabCounts = useMemo(
    () => statusCounts(campaigns, { ...closingContext, q: query, closing }),
    [campaigns, closingContext, query, closing],
  );
  const presetCounts = useMemo(
    () => closingCounts(campaigns, { ...closingContext, q: query, status }),
    [campaigns, closingContext, query, status],
  );

  const visibleCampaigns = useMemo(
    () =>
      campaigns.filter(
        (campaign) =>
          matchesSearch(campaign, query) &&
          matchesClosing(campaign, closing, closingContext) &&
          matchesStatus(reportStatusOf(campaign, today), status),
      ),
    [campaigns, query, closing, closingContext, status, today],
  );

  // For a MemberCard's report link: the member card grid isn't scoped to a
  // single campaign, so each member's link resolves to their MOST RECENT
  // campaign (sort desc by `startsAt`, take the first campaign that
  // includes them) — same "most recent" heuristic the dashboard uses for
  // "Latest evaluations".
  const mostRecentCampaignByEmployee = useMemo(() => {
    const map = new Map<EmployeeId, Campaign["id"]>();
    const sortedCampaigns = (campaignsPage?.content ?? []).toSorted((a, b) =>
      b.startsAt.localeCompare(a.startsAt),
    );

    for (const campaign of sortedCampaigns) {
      const members = campaignGroups.get(campaign.id)?.members ?? [];
      for (const member of members) {
        if (!map.has(member.uuid)) {
          map.set(member.uuid, campaign.id);
        }
      }
    }

    return map;
  }, [campaignsPage?.content, campaignGroups]);

  const filteredMembers = useMemo(() => {
    if (!membersPage) {
      return [];
    }

    const query = memberSearch.trim().toLowerCase();
    if (!query) {
      return membersPage.content;
    }

    return membersPage.content.filter((member) =>
      member.name.toLowerCase().includes(query),
    );
  }, [membersPage, memberSearch]);

  const hasAnyMembers = (membersPage?.content.length ?? 0) > 0;
  const hasNoFilteredMembers = hasAnyMembers && filteredMembers.length === 0;

  return (
    <div className="flex flex-col gap-9 pb-12">
      <div className="flex flex-col gap-6">
        <div
          className="
            flex flex-col gap-4 border-b border-primary-100 pb-6
            md:flex-row md:items-start md:justify-between
          "
        >
          <div className="flex flex-col gap-2">
            <h1 className="text-page-title">Campaign reports</h1>
            <p className="text-body2 text-neutral-600">
              Publish a report to share results with participants once an
              evaluation closes.
            </p>
          </div>
          <label
            className="
              relative block w-full
              md:w-80 md:shrink-0
            "
          >
            <Search
              aria-hidden
              className="
                pointer-events-none absolute top-1/2 left-3 -translate-y-1/2
                text-neutral-600
              "
              size="16"
            />
            <input
              aria-label="Search evaluations"
              className={`
                ${inputClassName}
                min-h-11 w-full pl-9
              `}
              onChange={(event) =>
                updateFilters({ q: event.target.value || undefined })
              }
              placeholder="Search evaluations"
              type="search"
              value={query}
            />
          </label>
        </div>

        {campaigns.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <ReportStatusTabs
              counts={tabCounts}
              onChange={(value) =>
                updateFilters({ status: value === "all" ? undefined : value })
              }
              value={status}
            />
            <ClosingDateFilter
              counts={presetCounts}
              onChange={(filter) =>
                updateFilters(closingFilterToSearch(filter))
              }
              value={closing}
              years={closingContext.listedYears}
            />
          </div>
        )}

        {renderCampaignReports({
          isLoadingCampaigns,
          isCampaignsError,
          onRetry: () => void refetchCampaigns(),
          hasAnyCampaigns: campaigns.length > 0,
          campaigns: visibleCampaigns,
          campaignGroups,
          today,
          onClearFilters: () => updateFilters(CLEARED_FILTERS),
        })}
      </div>

      <div className="flex flex-col gap-6">
        <DashboardSectionTitle>
          Reports by company members
        </DashboardSectionTitle>
        <div className="flex flex-col gap-1">
          <label
            className="text-body3 text-neutral-600"
            htmlFor="member-search"
          >
            Search
          </label>
          <input
            className={`
              ${inputClassName}
              max-w-sm
            `}
            id="member-search"
            onChange={(event) => setMemberSearch(event.target.value)}
            placeholder="Search by name"
            type="text"
            value={memberSearch}
          />
        </div>
        {renderMembers({
          isLoadingMembers,
          hasAnyMembers,
          hasNoFilteredMembers,
          filteredMembers,
          mostRecentCampaignByEmployee,
        })}
      </div>
    </div>
  );
}

interface CampaignReportsProps {
  isLoadingCampaigns: boolean;
  isCampaignsError: boolean;
  onRetry: () => void;
  hasAnyCampaigns: boolean;
  campaigns: Campaign[];
  campaignGroups: Map<Campaign["id"], CampaignGroupsState>;
  today: Date;
  onClearFilters: () => void;
}

const gridClassName = "grid-cards gap-6";

function renderCampaignReports(props: CampaignReportsProps) {
  const {
    isLoadingCampaigns,
    isCampaignsError,
    onRetry,
    hasAnyCampaigns,
    campaigns,
    campaignGroups,
    today,
    onClearFilters,
  } = props;

  if (isLoadingCampaigns) {
    return (
      <div className={gridClassName}>
        {SKELETON_KEYS.map((key) => (
          <CampaignReportCardSkeleton key={key} />
        ))}
      </div>
    );
  }

  if (isCampaignsError) {
    return (
      <div
        className="flex flex-wrap items-center gap-4 text-body2 text-error-300"
      >
        Couldn't load campaigns.
        <Button onClick={onRetry} size="sm" type="button">
          Try again
        </Button>
      </div>
    );
  }

  if (!hasAnyCampaigns) {
    return <div className="text-body2 text-neutral-600">No campaigns yet.</div>;
  }

  if (campaigns.length === 0) {
    return (
      <div
        className="
          flex flex-wrap items-center gap-4 text-body2 text-neutral-600
        "
      >
        No reports match your filters.
        <Button onClick={onClearFilters} size="sm" type="button">
          Clear filters
        </Button>
      </div>
    );
  }

  return (
    <ul aria-label="Campaign reports" className={gridClassName}>
      {campaigns.map((campaign) => (
        <li className="min-w-0" key={campaign.id}>
          <CampaignReportCard
            campaign={campaign}
            groups={campaignGroups.get(campaign.id) ?? PENDING_CAMPAIGN_GROUPS}
            today={today}
          />
        </li>
      ))}
    </ul>
  );
}

interface MembersProps {
  isLoadingMembers: boolean;
  hasAnyMembers: boolean;
  hasNoFilteredMembers: boolean;
  filteredMembers: Employee[];
  mostRecentCampaignByEmployee: Map<EmployeeId, Campaign["id"]>;
}

function renderMembers(props: MembersProps) {
  const {
    isLoadingMembers,
    hasAnyMembers,
    hasNoFilteredMembers,
    filteredMembers,
    mostRecentCampaignByEmployee,
  } = props;

  if (isLoadingMembers) {
    return <div className="text-body2 text-neutral-600">Loading...</div>;
  }

  if (!hasAnyMembers) {
    return (
      <div className="text-body2 text-neutral-600">No company members yet.</div>
    );
  }

  if (hasNoFilteredMembers) {
    return (
      <div className="text-body2 text-neutral-600">
        No members match your search.
      </div>
    );
  }

  return (
    <div className="grid-cards-compact gap-6">
      {filteredMembers.map((member) => {
        const campaignId = mostRecentCampaignByEmployee.get(member.uuid);
        const card = (
          <MemberCard
            member={member}
            {...(member.role ? { role: member.role } : {})}
          />
        );

        if (!campaignId) {
          return <div key={member.uuid}>{card}</div>;
        }

        return (
          <Link
            key={member.uuid}
            params={{ campaignId, employeeId: member.uuid }}
            to="/dashboard/admin/reports/$campaignId/$employeeId"
          >
            {card}
          </Link>
        );
      })}
    </div>
  );
}
